import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Notification {
  id: string;
  childId: string;
  type: 'badge' | 'level-up' | 'quest-complete' | 'streak-milestone' | 'shop-unlock' | 'place-favorited' | 'competition-challenge';
  title: string;
  description: string;
  emoji: string;
  timestamp: string; // ISO
  read: boolean;
}

interface NotificationsState {
  notifications: Notification[];
  addNotification: (n: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markRead: (id: string) => void;
  markAllRead: (childId: string) => void;
  getUnreadCount: (childId: string) => number;
  getNotifications: (childId: string) => Notification[];
}

const MAX_PER_CHILD = 50;

export const useNotificationsStore = create<NotificationsState>()(
  persist(
    (set, get) => ({
      notifications: [],

      addNotification: (n) => {
        const notification: Notification = {
          ...n,
          id: `notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          timestamp: new Date().toISOString(),
          read: false,
        };

        set((state) => {
          const updated = [notification, ...state.notifications];
          // Keep max 50 per child — trim oldest for this child
          const childNotifs = updated.filter((x) => x.childId === n.childId);
          if (childNotifs.length > MAX_PER_CHILD) {
            const excessIds = new Set(
              childNotifs.slice(MAX_PER_CHILD).map((x) => x.id)
            );
            return { notifications: updated.filter((x) => !excessIds.has(x.id)) };
          }
          return { notifications: updated };
        });
      },

      markRead: (id) => {
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        }));
      },

      markAllRead: (childId) => {
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.childId === childId ? { ...n, read: true } : n
          ),
        }));
      },

      getUnreadCount: (childId) => {
        return get().notifications.filter(
          (n) => n.childId === childId && !n.read
        ).length;
      },

      getNotifications: (childId) => {
        return get().notifications.filter((n) => n.childId === childId);
      },
    }),
    {
      name: 'homeschool-notifications',
      version: 0,
      migrate: (state) => state,
    }
  )
);
